import {
  DeepPartial,
  FlexibleBlockModifier,
  FlexibleBlockTemplate,
  PortalHeroConfig,
  PortalHeroType,
  TranslatesFnType,
  ViewItem,
} from '@nx-frontend/shared/types';
import { getWrapperProps } from '@nx-frontend/shared/utils';

interface HeroView extends ViewItem<PortalHeroType> {
  config: DeepPartial<PortalHeroConfig>;
  blocks: {
    template: FlexibleBlockTemplate;
    modifier?: FlexibleBlockModifier;
  }[];
}

export const getHeroViews = (t?: TranslatesFnType): HeroView[] => {
  const defaultConfig: DeepPartial<PortalHeroConfig> = {
    blocks: { rtl: false },
  };

  return [
    {
      key: 'empty',
      title: t ? t('hero-screen.empty_hero') : 'Empty hero',
      image: '/assets/img/controlbar/hero/view-empty-hero.png',
      config: { ...defaultConfig },
      blocks: [],
    },
    {
      key: 'search-and-greetings',
      title: t ? t('hero-screen.hero_type_2') : 'Hero with Image, Greeting & Search',
      image: '/assets/img/controlbar/hero/view-search-and-greetings-hero.png',
      preview: '/assets/img/controlbar/hero/preview-search-and-greetings-hero.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'hero-search', view: 'basic' },
          modifier: {
            colspan: 6,
            // bricks: [
            //   { brickType: 'title', theme: { text: { main: PortalTextType.HERO_TEXT } } },
            //   { brickType: 'text', theme: { text: { main: PortalTextType.HERO_TEXT } } },
            // ],
          },
        },
      ],
    },
    {
      key: 'right-side',
      title: t ? t('hero-screen.hero_type_3') : 'Hero with Image, Greeting & Search',
      image: '/assets/img/controlbar/hero/view-right-side-hero.png',
      preview: '/assets/img/controlbar/hero/preview-right-side-hero.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'image', view: 'basic' },
          modifier: {
            colspan: 3,
            bricks: [{ brickType: 'image', src: '/assets/img/blocks/image/vector-1.png', className: ['max-h-72'] }],
            className: ['max-w-lg', 'mx-auto'],
          },
        },
        {
          template: { type: 'hero-search', view: 'basic' },
          modifier: {
            colspan: 3,
          },
        },
      ],
    },
    {
      key: 'left-side',
      title: t ? t('hero-screen.hero_type_4') : 'Hero with Image & Headline, Search',
      image: '/assets/img/controlbar/hero/view-left-side-hero.png',
      preview: '/assets/img/controlbar/hero/preview-left-side-hero.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'hero-search', view: 'basic' },
          modifier: {
            colspan: 3,
          },
        },
        {
          template: { type: 'image', view: 'basic' },
          modifier: {
            colspan: 3,
            bricks: [{ brickType: 'image', src: '/assets/img/blocks/image/vector-2.png', className: ['max-h-72'] }],
            className: ['max-w-lg', 'mx-auto'],
          },
        },
      ],
    },

    {
      key: 'button-left',
      title: t ? t('hero-screen.hero_type_5') : 'Hero with Button and Headline',
      image: '/assets/img/controlbar/hero/view-button-left-hero.png',
      preview: '/assets/img/controlbar/hero/preview-button-left-hero.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'hero-impression', view: 'basic' },
          modifier: {
            colspan: 3,
            title: t ? t('hero-screen.hero_section_5') : 'Headline & Button',
          },
        },
      ],
    },
    {
      key: 'headline',
      title: t ? t('hero-screen.hero_type_6') : 'Hero with Headline & Image',
      image: '/assets/img/controlbar/hero/view-headline-hero.png',
      preview: '/assets/img/controlbar/hero/preview-headline-hero.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'hero-headline', view: 'basic' },
        },
      ],
    },
    {
      key: 'counsulting',
      title: t ? t('hero-screen.hero_type_7') : 'Hero with Headline & Icons',
      image: '/assets/img/controlbar/hero/view-counsulting-hero.png',
      preview: '/assets/img/controlbar/hero/preview-counsulting-hero.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'hero-counsalting', view: 'basic' },
          modifier: {
            colspan: 3,
            title: t ? t('hero-screen.hero_section_7') : 'Headline & Icons',
          },
        },
        {
          template: { type: 'image', view: 'basic' },
          modifier: {
            colspan: 3,
            bricks: [
              {
                brickType: 'image',
                enabled: false,
                src: '/assets/img/blocks/image/vector-2.png',
                className: ['max-h-72'],
              },
            ],
            className: ['max-w-lg', 'mx-auto'],
          },
        },
      ],
    },
    {
      key: 'events-1',
      title: t ? t('hero-screen.hero_type_8') : 'Hero with Images and Manager card',
      image: '/assets/img/controlbar/hero/view-events-1-hero.png',
      preview: '/assets/img/controlbar/hero/preview-events-1-hero.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'hero-event', view: 'basic' },
          modifier: {
            colspan: 6,
            title: t ? t('hero-screen.right_content') : 'Right content',
            className: ['justify-between'],
          },
        },
      ],
    },
    {
      hidden: false,
      key: 'carousel',
      title: t ? t('hero-screen.carousel') : 'Carousel',
      image: '/assets/img/controlbar/hero/view-hero-carousel.png',
      preview: '/assets/img/controlbar/hero/preview-hero-carousel.png',
      config: { ...defaultConfig },
      blocks: [
        {
          template: { type: 'carousel', view: 'basic' },
          modifier: {
            colspan: 6,
            title: t ? t('hero-screen.hero_carousel') : 'Hero carousel',
            className: ['p-1'],
            wrapper: getWrapperProps('none'),
          },
        },
      ],
    },
  ];
};

export const getSelectedHeroView = (selected: PortalHeroType, t?: TranslatesFnType): HeroView => {
  const views = getHeroViews(t);
  return views.find(({ key }) => key === selected) ?? views[0];
};
